/// <amd-module name="VSS/Features/MarkdownToolbar/TableMenuItem" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { Button, ExpandableButton } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { IExpandable } from "VSSUI/Expandable";
import { preventDefault } from "VSSUI/Util";
import { IOffset, IOrigin, IPoint } from "VSSUI/Utilities/Position";
import { Location } from "VSSUI/Utilities/Position";

import * as Resources from "VSS/Features/MarkdownToolbar/Resources";
import { TableGrid, TableSize } from "VSS/Features/MarkdownToolbar/TableGrid";
import { getFormattedContent, getTableString } from "VSS/Features/MarkdownToolbar/Utilities";

export interface ITableMenuItemProps {
    onTableSelected: (tableString: string, rowCount: number, colCount: number) => void;
    onMenuItemClicked: () => void;
    onTablesFormatted: (tablesCount: number, formattedContent?: string, selectionStart?: number, selectionEnd?: number) => void;
    buttonClassName?: string;
    iconClassName?: string;
    ariaPosInSet?: number;
    ariaSetSize?: number;
    getTextArea(): HTMLTextAreaElement;
}

export class TableMenuItem extends React.Component<ITableMenuItemProps> {
    public render(): JSX.Element {
        return (
            <ExpandableButton
                role="menuitem"
                className={this.props.buttonClassName}
                ariaLabel={Resources.TableMenuItemTooltip}
                ariaPosInSet={this.props.ariaPosInSet}
                ariaSetSize={this.props.ariaSetSize}
                tooltipProps={{
                    text: Resources.TableMenuItemTooltip,
                    anchorOffset: { horizontal: 0, vertical: -10 },
                    anchorOrigin: { horizontal: Location.center, vertical: Location.start },
                    tooltipOrigin: { horizontal: Location.center, vertical: Location.end }
                }}
                iconProps={{ iconName: "Table", className: this.props.iconClassName }}
                hideDropdownIcon
                renderCallout={this._onRenderInsertTableDropdown}
            />
        );
    }

    private _onRenderInsertTableDropdown = (
        dropdown: IExpandable,
        dropdownId: string,
        anchorElement?: HTMLElement,
        anchorOffset?: IOffset,
        anchorOrigin?: IOrigin,
        anchorPoint?: IPoint,
        dropdownOrigin?: IOrigin
    ): JSX.Element => {
        this.props.onMenuItemClicked();

        const onDismissCallout = (): void => {
            dropdown.collapse();
        };

        const onTableSelected = (tableSize: TableSize): void => {
            dropdown.collapse();
            const { rowCount, colCount } = tableSize;
            this.props.onTableSelected(getTableString(rowCount, colCount), rowCount, colCount);
        };

        const onFormatTables = (): void => {
            dropdown.collapse();
            const textArea = this.props.getTextArea();
            const { tablesCount, content, selectionStart, selectionEnd } = getFormattedContent(
                textArea.value,
                textArea.selectionStart,
                textArea.selectionEnd
            );
            this.props.onTablesFormatted(tablesCount, content, selectionStart, selectionEnd);
        };

        return (
            <Callout
                anchorElement={anchorElement}
                anchorOffset={anchorOffset}
                anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                anchorPoint={anchorPoint}
                calloutOrigin={{ horizontal: Location.start, vertical: Location.start }}
                onDismiss={onDismissCallout}
                contentClassName="table-options-callout flex-column"
                contentShadow
                blurDismiss
                escDismiss
            >
                <InsertTableCallout onTableSelected={onTableSelected} onFormatTables={onFormatTables} />
            </Callout>
        );
    };
}

interface IInsertTableCalloutProps {
    onTableSelected: (selectedTableSize: TableSize) => void;
    onFormatTables: () => void;
}

// export for UTs
export class InsertTableCallout extends React.Component<IInsertTableCalloutProps> {
    public render(): JSX.Element {
        return (
            <div onMouseDown={preventDefault}>
                <div className="insert-to">
                    <TableGrid onActivated={this.props.onTableSelected} />
                </div>
                <div className="to-separator" />
                <div className="format-to flex-column">
                    <Button
                        className="justify-start ft-button flex-grow"
                        iconProps={{ iconName: "AlignJustify" }}
                        text={Resources.FormatTablesLabel}
                        subtle
                        onClick={this.props.onFormatTables}
                    />
                </div>
            </div>
        );
    }
}
